import React, { useEffect, useState } from 'react';
import { Label } from '@atlaskit/form';
import { Box } from '@atlaskit/primitives';
import ProgressBar from '@atlaskit/progress-bar';
import Spinner from '@atlaskit/spinner';
import TableTree from '@atlaskit/table-tree';
import { events, view } from '@forge/bridge';

import { OptionItemObject } from '../../../../../../src/shared_models/interfaces';
import StorageApi from '../../../api/storageApi';
import { ValueLabel } from '../../../components/config/options-adder/interfaces';
import { useIsLicenseActive } from '../../../hooks/useIsLicenseActive';
import useMultilevelFields from '../../../hooks/useMlvFields';
import { getCustomFieldConfigurationId } from '../../../service/contextService';
import { getDefaultSchemePriorities } from '../../../service/prioritySchemeService';
import { getFieldTreeData, hasValidObjectLength } from '../../../utils/levelModelUtils';
import { LicenseBanner } from '../../../utils/LicenseBanner';
import GroupingSelect from '../components/GroupingSelect';
import {
    ASSIGNEE,
    DEFAULT_CONFIG,
    DEFAULT_FETCHING_PROGRESS,
    GADGET_DATA_FETCHED,
    GROUP_NAME_TO_INDEX,
    JIRA_DASHBOARD_GADGET_REFRESH,
    PRIORITY,
} from '../constants';
import cacheClient from '../GadgetCacheClient';
import { AggregatedReport, FetchingProgress, GadgetConfig, GadgetContext, GadgetRefreshedPayload } from '../model';
import {TableTreeWrapper, TableWrapper, ViewWrapper} from '../styled';

import { getIssueFieldsByJqlForGadget } from './issueSearchService';
import { Content, getAllUsersByAccountIds, Item, prepareViewData } from './viewUtils';



const View = () => {
    const [fieldOptions, setFieldOptions] = useState<OptionItemObject[]>([]);
    const [groupingOption, setGroupingOption] = useState(PRIORITY);
    const [gadgetContext, setGadgetContext] = useState<GadgetContext>();
    const [displayItems, setDisplayItems] = useState<Item[]>([]);
    const [aggregatedReport, setAggregatedReport] = useState<AggregatedReport>({});
    const [fetchingProgress, setFetchingProgress] = useState<FetchingProgress>(DEFAULT_FETCHING_PROGRESS);
    const { isLicenseActive } = useIsLicenseActive();
    const [multilevelFields, isLoading] = useMultilevelFields(field => ({ label: field.name, value: field.id }) as ValueLabel);
    const [isDataFetched, setIsDataFetched] = useState(false);
    const [isLoadingView, setIsLoadingView] = useState(true);
    const [columns, setColumns] = useState<string[]>([]);
    const [headers, setHeaders] = useState<string[]>([]);
    const [columnWidths, setColumnWidths] = useState<string[]>([]);
    const [totalTableWidth, setTotalTableWidth] = useState<string>('');

    const fetchData = async () => {
        const context = (await view.getContext()) as GadgetContext;
        const configValue =
            hasValidObjectLength(context.extension?.gadgetConfiguration) ?
                context.extension.gadgetConfiguration
                :   DEFAULT_CONFIG;
        setGadgetContext(context);
        setGroupingOption(configValue.groupingFieldId);
        const globalConfigurationValue = await getCustomFieldConfigurationId(configValue.fieldId);
        const fieldTreeData =
            globalConfigurationValue ?
                (getFieldTreeData(
                    await StorageApi.getConfig(configValue.fieldId, globalConfigurationValue)
                ) as OptionItemObject[])
                :   [];
        setFieldOptions(fieldTreeData);
        const cachedData = await cacheClient.get(context);
        if (cachedData) {
            setAggregatedReport(cachedData);
            await recalculateAndRefreshTableView(configValue.groupingFieldId, fieldTreeData, configValue, cachedData);
        } else {
            await subscribeRefresh({
                gadgetId: context.extension.gadget.id,
                gadgetContext: context,
                groupingOption: configValue.groupingFieldId,
                fieldOptions: fieldTreeData,
                origin: 'gadget',
                startEvent: true,
            });
        }
        setIsDataFetched(true);
    };

    const subscribeRefresh = async (payload: GadgetRefreshedPayload) => {
        const [gadgetContextValue, gadgetId, groupingOptionValue, fieldOptionsValue] =
            payload.startEvent ?
                [payload.gadgetContext, payload.gadgetId, payload.groupingOption, payload.fieldOptions]
                :   [gadgetContext, payload.gadgetId, groupingOption, fieldOptions];

        if (isLicenseActive && gadgetContextValue && gadgetContextValue?.extension.gadget.id === gadgetId) {
            const newData = await getIssueFieldsByJqlForGadget(gadgetContextValue);
            setAggregatedReport(newData);
            setGroupingOption(groupingOptionValue!);
            const configValue = gadgetContextValue.extension.gadgetConfiguration;
            await recalculateAndRefreshTableView(
                groupingOptionValue!,
                fieldOptionsValue as OptionItemObject[],
                configValue,
                newData
            );
        }
    };

    const subscribeFetching = (payload: FetchingProgress) => {
        if (gadgetContext?.extension.gadget.id === payload.gadgetId) {
            setFetchingProgress(payload);
        }
    };

    useEffect(() => {
        if (!isLoading) {
            void fetchData();
        }
    }, [isLoading]);

    useEffect(() => {
        if (isDataFetched) {
            const refreshSubscription = events.on(JIRA_DASHBOARD_GADGET_REFRESH, subscribeRefresh);
            const fetchingSubscription = events.on(GADGET_DATA_FETCHED, subscribeFetching);
            return () => {
                void refreshSubscription.then(({ unsubscribe }) => unsubscribe());
                void fetchingSubscription.then(({ unsubscribe }) => unsubscribe());
            };
        }
    }, [isDataFetched]);

    const onGroupingOptionChange = async (groupingOption: string) => {
        setGroupingOption(groupingOption);

        const config = gadgetContext?.extension.gadgetConfiguration;
        if (!groupingOption || !hasValidObjectLength(config) || !config) {
            return;
        }
        await recalculateAndRefreshTableView(groupingOption, fieldOptions, config, aggregatedReport);
    };


    const getTextWidth = (text: string, font: string = '16px Arial'): number => {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (context) {
            context.font = font;
            return context.measureText(text).width;
        }
        return 0;
    };

    const findMaxLevelNameWidth = (items: Item[], depth: number = 0): number => {
        let maxWidth = 0;
        items.forEach(item => {
            const levelNameWidth = getTextWidth(item.content.levelName);
            const levelPadding = 25;
            const extraRightPadding = 24;
            const width = levelPadding * depth + levelNameWidth + extraRightPadding;
            if (width > maxWidth) {
                maxWidth = width;
            }
            if (item.children && item.children.length > 0) {
                const childMaxWidth = findMaxLevelNameWidth(item.children, depth + 1);
                if (childMaxWidth > maxWidth) {
                    maxWidth = childMaxWidth;
                }
            }
        });
        return maxWidth;
    };
    const handleTableRelatedWidths = (refreshedView: Item[], headers: string[]) => {
        const maxLevelNameWidth = findMaxLevelNameWidth(refreshedView);
        const levelColumnWidth = `${maxLevelNameWidth}px`;
        const paddingSum = 48;

        const otherColumnWidths = headers.map(header => `${getTextWidth(header) + paddingSum}px`);

        const totalWidth = [levelColumnWidth, ...otherColumnWidths].reduce((acc, width) => acc + parseInt(width), 0);
        setTotalTableWidth(`${totalWidth}px`);

        setColumnWidths([levelColumnWidth, ...otherColumnWidths]);
    };

    const recalculateAndRefreshTableView = async (
        groupBy: string,
        fieldTreeData: OptionItemObject[],
        config: GadgetConfig,
        aggregatedData: AggregatedReport
    ) => {
        setIsLoadingView(true);
        const fieldName = multilevelFields.find(field => field.value === config.fieldId)?.label || '';
        const { columns, headers } = await getColumnsAndHeadersBasedOnGrouping(groupBy, aggregatedData);
        setColumns(columns);
        setHeaders(headers);

        const refreshedView = prepareViewData(
            fieldTreeData,
            aggregatedData,
            groupBy,
            fieldName,
            config.jql,
            [...columns],
            config.hideEmptyValues
        );
        setDisplayItems(refreshedView);

        handleTableRelatedWidths(refreshedView, headers);
        setIsLoadingView(false);
    };

    const getColumnsAndHeadersBasedOnGrouping = async (groupBy: string, aggregatedData: AggregatedReport) => {
        const groupingIndex = GROUP_NAME_TO_INDEX[groupBy];
        const newColumns = [...new Set(Object.values(aggregatedData).flatMap(item => Object.keys(item[groupingIndex])))];
        let headers: string[];
        let columns: string[];

        if (groupBy === ASSIGNEE) {
            const users = await getAllUsersByAccountIds(newColumns);
            headers = newColumns.map(column => users[column] || column);
            columns = newColumns;
        } else if (groupBy === PRIORITY) {
            const defaultSchemePriorities = await getDefaultSchemePriorities();
            headers = [...new Set([...defaultSchemePriorities, ...newColumns])];
            columns = [...new Set([...defaultSchemePriorities, ...newColumns])];
        } else {
            headers = newColumns;
            columns = newColumns;
        }

        return { headers, columns };
    };

    const LevelName = (props: Content) => <Box as='span'>{props.levelName}</Box>;
    const GroupingColumn = (groupingContent: string) => {
        return (props: Content) => <Box as='span'>{props[groupingContent]}</Box>;
    };

    return (
        <LicenseBanner isLicenseValid={isLicenseActive}>
            <ViewWrapper>
                <Label>
                    {multilevelFields.find(field => field.value === gadgetContext?.extension.gadgetConfiguration.fieldId)
                        ?.label || ''}
                </Label>
                <GroupingSelect
                    value={groupingOption}
                    onChange={onGroupingOptionChange}
                    label='Group by'
                />
                <TableWrapper>
                    <TableTreeWrapper forcedTableWidth={totalTableWidth}>

                        {fetchingProgress.isFetching ?
                            <div>
                                <Label htmlFor='mlv-field-jql'>
                                    Done {fetchingProgress.currentNumber} of {fetchingProgress.total}
                                </Label>
                                <ProgressBar value={fetchingProgress.currentNumber / fetchingProgress.total}/>
                            </div>
                            : isLicenseActive &&
                            (isLoadingView ?
                                <Spinner/>
                                : <TableTree
                                    key={groupingOption}
                                    columns={[LevelName, ...columns.map(curr => GroupingColumn(curr))]}
                                    headers={['Level', ...headers]}
                                    columnWidths={columnWidths}
                                    items={displayItems}
                                />)
                        }

                    </TableTreeWrapper>
                </TableWrapper>
        </ViewWrapper>
</LicenseBanner>
    );
};

export default View;